import type { Icon } from '@phosphor-icons/react'
import type { ReactNode } from 'react'
import { HOVER_REVEAL_LABEL_CLASSES } from '@/components/shared/hover-reveal'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { FOCUS_RING_INSET } from '@/lib/focus-ring'
import { cn } from '@/lib/utils'
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group'
import { Link } from 'react-router-dom'

export interface SegmentedControlItem<T extends string = string> {
  value: T
  label: ReactNode
  icon?: Icon
  /** Link item: renders a Link so the view stays a URL segment. */
  to?: string
  disabled?: boolean
  ariaLabel?: string
  /**
   * Shown after the label and always visible, also in iconOnly mode (a
   * count, say). Include it in `ariaLabel` if it should be announced.
   */
  badge?: ReactNode
  /** Hover/focus explanation of the choice (what a metric means, say). */
  tooltip?: ReactNode
}

export interface SegmentedControlProps<T extends string = string> {
  items: SegmentedControlItem<T>[]
  value: T
  /** Fired for button items, and for link items after navigation so callers can persist a preference. */
  onValueChange?: (value: T) => void
  'aria-label': string
  /** Stretch items across the full width (phones). */
  fill?: boolean
  /**
   * Show each item as its icon; the label slides out beside it on hover or
   * keyboard focus (the detail-header hover-reveal) and is the item's
   * accessible name. Every item needs an icon and a string label.
   */
  iconOnly?: boolean
  className?: string
}

/**
 * The app's one segmented control for switching views of the same data
 * (design audit S8): 28 px track, 12 px labels. Items are either buttons
 * (value/onValueChange) or links (`to`), never mixed in one control.
 */
export function segmentedControlItemClasses(active: boolean, fill = false) {
  return cn(
    'inline-flex h-6 items-center gap-1 whitespace-nowrap rounded-md px-2.5 text-xs font-medium transition-colors',
    FOCUS_RING_INSET,
    active
      ? 'bg-background text-foreground shadow-sm'
      : 'text-muted-foreground hover:text-foreground',
    fill && 'flex-1 justify-center',
    'disabled:pointer-events-none disabled:opacity-50',
  )
}

const TRACK_CLASSES =
  'inline-flex h-7 items-center gap-0.5 rounded-lg bg-muted p-0.5'

/** An icon-only item: tighter padding, no gap until the label slides out. */
const ICON_ONLY_ITEM_CLASSES = 'group/reveal gap-0 px-2'

export function SegmentedControl<T extends string = string>({
  items,
  value,
  onValueChange,
  'aria-label': ariaLabel,
  fill = false,
  iconOnly = false,
  className,
}: SegmentedControlProps<T>) {
  const isLinkControl = items.some(item => item.to !== undefined)

  const renderContent = (item: SegmentedControlItem<T>) => {
    const ItemIcon = item.icon
    if (iconOnly && ItemIcon) {
      return (
        <>
          <ItemIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className={HOVER_REVEAL_LABEL_CLASSES}>{item.label}</span>
          {item.badge != null && <span className="ml-1">{item.badge}</span>}
        </>
      )
    }
    return (
      <>
        {ItemIcon && <ItemIcon className="h-3.5 w-3.5" />}
        {item.label}
        {item.badge}
      </>
    )
  }
  const itemAriaLabel = (item: SegmentedControlItem<T>) =>
    item.ariaLabel ??
    (iconOnly && typeof item.label === 'string' ? item.label : undefined)
  const withTooltip = (item: SegmentedControlItem<T>, element: ReactNode) =>
    item.tooltip == null ? (
      element
    ) : (
      <Tooltip key={item.value}>
        <TooltipTrigger asChild>{element}</TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-56">
          {item.tooltip}
        </TooltipContent>
      </Tooltip>
    )

  if (isLinkControl) {
    return (
      <nav
        aria-label={ariaLabel}
        className={cn(TRACK_CLASSES, fill && 'flex w-full', className)}
      >
        {items.map(item => {
          const active = item.value === value
          return withTooltip(
            item,
            <Link
              key={item.value}
              to={item.to ?? '#'}
              aria-current={active ? 'page' : undefined}
              aria-label={itemAriaLabel(item)}
              aria-disabled={item.disabled || undefined}
              tabIndex={item.disabled ? -1 : undefined}
              onClick={event => {
                if (item.disabled) {
                  event.preventDefault()
                  return
                }
                onValueChange?.(item.value)
              }}
              className={cn(
                segmentedControlItemClasses(active, fill),
                iconOnly && ICON_ONLY_ITEM_CLASSES,
                item.disabled && 'pointer-events-none opacity-50',
              )}
            >
              {renderContent(item)}
            </Link>,
          )
        })}
      </nav>
    )
  }

  return (
    <ToggleGroupPrimitive.Root
      type="single"
      value={value}
      onValueChange={next => {
        // Radix emits an empty string when the active item is clicked again;
        // a segmented control always has one selection.
        if (next) onValueChange?.(next as T)
      }}
      aria-label={ariaLabel}
      className={cn(TRACK_CLASSES, fill && 'flex w-full', className)}
    >
      {items.map(item => {
        return withTooltip(
          item,
          <ToggleGroupPrimitive.Item
            key={item.value}
            value={item.value}
            disabled={item.disabled}
            aria-label={itemAriaLabel(item)}
            className={cn(
              segmentedControlItemClasses(item.value === value, fill),
              iconOnly && ICON_ONLY_ITEM_CLASSES,
            )}
          >
            {renderContent(item)}
          </ToggleGroupPrimitive.Item>,
        )
      })}
    </ToggleGroupPrimitive.Root>
  )
}
